import { T, tt } from '@agos/shared'
import { ConfirmDialog } from '@agos/ui'
import { Ban } from 'lucide-react'
import { useState } from 'react'

import type { UnprintedClose } from './split-payment'

/**
 * *Zatvoriť bez fiškálneho dokladu* - one question and an optional reason. `FRONTEND.md` §9.687, owner
 * #12a (O64).
 *
 * **The owner, through the operator**: *"bez zadávania prihlasovacieho údajov a zadania dôvodu len
 * zatvoriť bez fiškálneho a zatvorí to stôl"*. `OWNER-QUESTIONS.md` #116: no login, the reason optional,
 * and the signed-in employee recorded - `BACKEND.md` §10A.346, which checks that session's own
 * `fiskal` or `fiskal_storno` right.
 *
 * **It replaces `EmployeeAuthModal` on this action and nothing else.** That prompt asked for an e-mail, a
 * password and a reason (`authActionNeedsReason` answered true for this action whatever the caller said),
 * which was §9.447's design under owner #60's option (c) rather than anything the owner asked for.
 *
 * **`ConfirmDialog`, the component 52 screens use**, so the focus trap, the Escape and the cancel target
 * are §9.441's rather than a fourth copy of them - and while `busy` it cannot be dismissed, because a
 * close in flight is a write.
 *
 * **The reason box is optional and says so in its label**, because a required-looking box nobody has to
 * fill is the ceremony #116 removed by another route. What is typed reaches *História zmien*; nothing
 * typed is recorded as no reason, under the name of whoever is signed in.
 *
 * **§9.726, owner O96 - the button says what this channel will do**, from the one answer the note under the
 * button is written from (`unprintedCloseOf`), so the two cannot disagree.
 */
export function CloseWithoutDocumentConfirm({
  title,
  note,
  close,
  busy,
  onConfirm,
  onCancel,
}: {
  /** `closeWithoutDocumentTitle` - the consequence, the table and the amount. */
  title: string
  /** `closeWithoutDocumentNote` - what will and will not exist, said before the press. */
  note: string
  /**
   * What the press does - `unprintedCloseOf`. A *Do ruky* sale whose channel cancels it (§9.457) gets the stronger,
   * red button; one whose channel settles it (§10A.374, Sereď) closes the order rather than a table it has not got.
   */
  close: UnprintedClose
  /** The close is in flight. */
  busy: boolean
  /** Close, with what was typed - trimmed, and empty when nothing was. */
  onConfirm: (reason: string) => void
  onCancel: () => void
}) {
  const [reason, setReason] = useState('')

  return (
    <ConfirmDialog
      open
      variant={close === 'cancels' ? 'danger' : 'default'}
      icon={<Ban className="w-5 h-5" />}
      title={title}
      description={note}
      confirmLabel={confirmLabelOf(close)}
      cancelLabel={tt('Späť')}
      loading={busy}
      onConfirm={() => onConfirm(reason.trim())}
      onClose={busy ? () => undefined : onCancel}
    >
      <label className="block space-y-1">
        <span className="text-[11px] font-bold text-muted-foreground">
          <T>Dôvod (nepovinné)</T>
        </span>
        <textarea
          data-close-without-document-reason
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
          placeholder={tt('Napríklad: tlačiareň nefungovala')}
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs"
        />
      </label>
    </ConfirmDialog>
  )
}

/**
 * The button that commits the close - §9.726. A `switch` with no `default`, so a fourth {@link UnprintedClose} is
 * TS2366 rather than a button borrowing another answer's words.
 */
function confirmLabelOf(close: UnprintedClose): string {
  switch (close) {
    case 'cancels':
      return tt('Zrušiť objednávku')
    case 'settles-sale':
      // The verb the note uses (*uzavrie*), on the order rather than on a table a counter sale does not have.
      return tt('Uzavrieť objednávku')
    case 'settles-table':
      return tt('Zatvoriť stôl')
  }
}
